import { lazy } from "react";
import { matchPath } from "react-router";

import { Route } from "../types";

import EcoBank from "../pages/EcoBank";
import Explore from "../pages/Explore/index";
import NotFound from "../pages/NotFound";

// Every page but the two landing routes is its own chunk, fetched when its route first renders
// (App.tsx's <Suspense>). Statically imported, all of them sat in the main bundle, so `/` and
// `/explore` downloaded and evaluated every other page before their first paint. A page that is
// also imported statically elsewhere (LandPage from Explore, say) still ships in main.
const Profile = lazy(() => import("../pages/Profile"));
const Verification = lazy(() => import("../pages/LandVerification"));
const LandPage = lazy(() => import("../pages/LandPage"));
const Account = lazy(() => import("../pages/Profile/Account"));
const Assets = lazy(() => import("../pages/Profile/Assets"));
const Scan = lazy(() => import("../pages/Scan"));
const Dashboard = lazy(() => import("../pages/Profile/Dashboard"));
const Lands = lazy(() => import("../pages/EcoBank/Lands"));
const Transactions = lazy(() => import("../pages/EcoBank/Transactions"));
const TrustLands = lazy(() => import("../pages/Profile/TrustLands"));
const Verifications = lazy(() => import("../pages/Profile/Verifications"));
const AuctionCreation = lazy(() => import("../pages/AuctionCreation"));
const MapPage = lazy(() => import("../pages/Map"));
// import About from "../pages/About";
const TrustWallet = lazy(() => import("../pages/EcoBank/TrustWallet"));
const UserWallet = lazy(() => import("../pages/EcoBank/UserWallet"));
const LandTransactions = lazy(() => import("../pages/EcoBank/LandTransactions"));
const BecomeTrust = lazy(() => import("../pages/BecomeTrust"));
const ConfirmKyc = lazy(() => import("../pages/ConfirmKyc"));
const ClimatePage = lazy(() => import("../pages/ClimatePage"));
const AdminOrganizations = lazy(() => import("../pages/Profile/AdminOrganizations"));
const SingleOrganization = lazy(() => import("../pages/Profile/SingleOrganization"));
const Test = lazy(() => import("../pages/Profile/Test"));
const Portal = lazy(() => import("../pages/Profile/Portal"));
const ApplicantReviewPage = lazy(() => import("../pages/Profile/ApplicantReview"));
const TestPage = lazy(() => import("../pages/TestPage"));
const SupportCenter = lazy(() => import("../pages/SupportCenter"));
const GuardianPortal = lazy(() => import("../pages/Profile/GuardianPortal"));
const Glossary = lazy(() => import("../pages/Glossary"));
const OfferCreation = lazy(() => import("../pages/OfferCreate"));
const ProfileCacheDemo = lazy(() => import("../pages/Profile/ProfileCacheDemo"));
const GuardianTransfer = lazy(() => import("../pages/GuardianTransfer"));
const HelpCenter = lazy(() => import("../pages/HelpCenter"));
const ShuftiComplete = lazy(() => import("../pages/ShuftiComplete"));
const DocumentSigner = lazy(() => import("../pages/DocumentSigner"));
const AppRoutes = {
  TestPage: {
    key: "testPage",
    title: "TestPage",
    component: TestPage,
    path: "/test-page",
    isEnabled: true,
  },
  Home: {
    key: "router-home",
    title: "Home",
    component: EcoBank,
    path: "/",
    isEnabled: true,
  },
  Test: {
    key: "test-home",
    title: "Auction ender",
    component: Test,
    path: "/test-auction-ender",
    isEnabled: true,
  },
  Token: {
    key: "router-token",
    title: "Asset",
    component: LandPage,
    path: "/asset/:tokenId",
    isEnabled: true,
  },
  Upload: {
    key: "router-upload",
    title: "Upload your land",
    component: Scan,
    path: "/upload",
    isEnabled: true,
    requireUser: true,
  },
  EcoBank: {
    key: "router-ecobank",
    title: "Terminal",
    component: EcoBank,
    path: "/",
    isEnabled: true,
  },
  Lands: {
    key: "router-lands",
    title: "Latest lands",
    component: Lands,
    path: "/lands",
    isEnabled: true,
  },
  Transactions: {
    key: "router-transactions",
    title: "Latest transactions",
    component: Transactions,
    path: "/transactions",
    isEnabled: true,
  },
  ClimatePage: {
    key: "router-climate",
    title: "Climate Change",
    component: ClimatePage,
    path: "/climate",
    isEnabled: true,
  },
  UserWallet: {
    key: "router-wallet-user",
    title: "User Wallet",
    component: UserWallet,
    path: "/wallet-user/:address",
    isEnabled: true,
  },
  TrustWallet: {
    key: "router-wallet-trust",
    title: "Trust Wallet",
    component: TrustWallet,
    path: "/wallet-trust/:address",
    isEnabled: true,
  },
  LandTransactions: {
    key: "router-wallet-land",
    title: "Land Transactions",
    component: LandTransactions,
    path: "/land-transactions/:nftId",
    isEnabled: true,
  },
  LandDetails: {
    key: "router-land-details",
    title: "Asset",
    component: LandPage,
    path: "/asset/:tokenId",
    isEnabled: true,
  },
  AuctionCreation: {
    key: "router-auction-create",
    title: "Auction Creation",
    component: AuctionCreation,
    path: "/create-auction/:tokenId",
    isEnabled: true,
    requireUser: true,
    requireTrust: false,
  },
  OfferCreation: {
    key: "router-offer-create",
    title: "Offer Creation",
    component: OfferCreation,
    path: "/create-offer/:tokenId",
    isEnabled: true,
    requireUser: true,
  },
  GuardianTransfer: {
    key: "router-guardian-transfer",
    title: "Guardian Transfer",
    component: GuardianTransfer,
    path: "/guardian-transfer/:tokenId",
    isEnabled: true,
    requireUser: true,
    requireTrust: true,
  },
  Verification: {
    key: "router-verification",
    title: "Land Verification",
    component: Verification,
    path: "/verification/:tokenId",
    isEnabled: true,
    requireUser: true,
    // Not trust-only: a Trust Admin / GTA judges a standing challenge here. The page decides what
    // each role may do, and GET /trust/tokens/:id decides what each may read.
  },
  Explore: {
    key: "router-explore",
    title: "Explore",
    component: Explore,
    path: "/explore",
    isEnabled: true,
  },
  Map: {
    key: "router-map",
    title: "Map",
    component: MapPage,
    path: "/map",
    isEnabled: false,
  },
  NotFound: {
    key: "router-404",
    title: "Not Found",
    component: NotFound,
    path: "/404",
    isEnabled: true,
  },
  About: {
    key: "router-about",
    title: "Help",
    component: SupportCenter,
    path: "/help",
    isEnabled: false,
  },
  Help: {
    key: "router-help-center",
    title: "Help",
    component: HelpCenter,
    path: "/help",
    isEnabled: true,
  },
  SupportCenter: {
    key: "router-supportCenter",
    title: "Help",
    component: SupportCenter,
    path: "/resource-center2",
    isEnabled: false,
  },
  BecomeTrust: {
    key: "router-trust",
    title: "Become Trustee",
    component: BecomeTrust,
    path: "/kyc-verification",
    isEnabled: true,
  },
  ConfirmKyc: {
    key: "router-confirm-kyc",
    title: "Confirm KYC",
    component: ConfirmKyc,
    path: "/confirm-kyc",
    // No requireUser guard: the auction winner is usually a regular holder (no `role`),
    // and the guard bounces role-less users off requireUser routes. The page handles the
    // unauthenticated case itself (connect-wallet prompt), like the Become-Trustee page.
    isEnabled: true,
  },
  Profile: {
    key: "router-profile",
    title: "Profile",
    component: Profile,
    path: "/dashboard",
    isEnabled: true,
    requireUser: true,
    subRoutes: {
      Dashboard: {
        key: "router-profile-dashboard",
        title: "Profile Wallet",
        component: Dashboard,
        path: "/dashboard/wallet",
        isEnabled: true,
        requireUser: true,
      },
      Identification: {
        key: "router-profile-identification",
        title: "Profile Account",
        component: Account,
        path: "/dashboard/account",
        isEnabled: true,
        requireUser: true,
      },
      Organizations: {
        key: "router-profile-organizations",
        title: "Trust Organizations",
        component: AdminOrganizations,
        path: "/dashboard/trusts",
        isEnabled: true,
        requireUser: true,
      },
      Organization: {
        key: "router-profile-organization",
        title: "Trust Organization",
        component: SingleOrganization,
        path: "/dashboard/organization/:id",
        isEnabled: true,
        requireUser: true,
        requireTrust: true,
      },
      AdminOrganization: {
        key: "router-admin-organization",
        title: "Admin Trust Organization",
        component: SingleOrganization,
        path: "/dashboard/trusts/:id",
        isEnabled: true,
        requireUser: true,
      },
      Guarding: {
        key: "router-profile-guarding",
        title: "Profile Lands",
        component: Assets,
        path: "/dashboard/lands",
        isEnabled: true,
        requireUser: true,
      },
      Collection: {
        key: "router-profile-assets",
        title: "Profile Portfolio",
        component: GuardianPortal,
        path: "/dashboard/portfolio",
        isEnabled: true,
        requireUser: true,
      },
      AdminPortal: {
        key: "router-admin-portal",
        title: "Admin Portal",
        component: Portal,
        path: "/dashboard/portal",
        isEnabled: true,
        requireUser: true,
      },
      ApplicantReview: {
        key: "router-admin-applicant-review",
        title: "Verifier Application Review",
        component: ApplicantReviewPage,
        path: "/dashboard/portal/review/:address",
        isEnabled: true,
        // Same guard as the Portal it opens from; the page adds the country-admin check itself.
        requireUser: true,
      },
      Lands: {
        key: "router-profile-lands",
        title: "Unclaimed Lands",
        component: TrustLands,
        path: "/dashboard/unclaimed",
        isEnabled: true,
        requireUser: false,
        requireTrust: true,
      },
      Verification: {
        key: "router-profile-verification",
        title: "Lands Verification",
        component: Verifications,
        path: "/dashboard/verification",
        isEnabled: true,
        requireUser: true,
        requireTrust: true,
      },
      CacheDemo: {
        key: "router-profile-cache-demo",
        title: "Cache Demo",
        component: ProfileCacheDemo,
        path: "/dashboard/cache-demo",
        isEnabled: true,
        requireUser: true,
      },
    },
  },
  Glossary: {
    key: "router-glossary",
    title: "Glossary",
    component: Glossary,
    path: "/glossary",
    isEnabled: true,
  },
  ShuftiComplete: {
    key: "router-shufti-complete",
    title: "Shufti Complete",
    component: ShuftiComplete,
    path: "/shufti-complete",
    isEnabled: true,
  },
  DocumentSigner: {
    key: "router-document-signer",
    title: "Sign Document",
    component: DocumentSigner,
    path: "/document-signer/:documentId",
    isEnabled: true,
    // Both trusts (from Verifications) and admins (from the Portal) sign documents
    // here, so gate on a logged-in profile rather than the trust-only requireTrust,
    // which would bounce admins to Home (see useAuthorization.fetchUser).
    requireUser: true,
  },
};

const AppRoutesList: Array<Route> = [
  AppRoutes.TestPage,
  AppRoutes.Test,
  AppRoutes.Home,
  AppRoutes.Explore,
  AppRoutes.EcoBank,
  AppRoutes.Lands,
  AppRoutes.Transactions,
  AppRoutes.Map,
  AppRoutes.NotFound,
  AppRoutes.About,
  AppRoutes.Help,
  AppRoutes.BecomeTrust,
  AppRoutes.ConfirmKyc,
  AppRoutes.Profile,
  AppRoutes.Token,
  AppRoutes.Upload,
  AppRoutes.ClimatePage,
  AppRoutes.Verification,
  AppRoutes.UserWallet,
  AppRoutes.LandTransactions,
  AppRoutes.TrustWallet,
  AppRoutes.LandDetails,
  AppRoutes.AuctionCreation,
  AppRoutes.SupportCenter,
  AppRoutes.Glossary,
  AppRoutes.OfferCreation,
  AppRoutes.GuardianTransfer,
  AppRoutes.Profile.subRoutes.Dashboard,
  AppRoutes.Profile.subRoutes.Identification,
  AppRoutes.Profile.subRoutes.Collection,
  AppRoutes.Profile.subRoutes.Guarding,
  AppRoutes.Profile.subRoutes.Lands,
  AppRoutes.Profile.subRoutes.Verification,
  AppRoutes.ShuftiComplete,
  AppRoutes.DocumentSigner,
];

// Title lookup only — deliberately separate from `AppRoutesList`, which drives
// <Route> registration in App.tsx. Flattening sub-routes here (rather than into
// that list) keeps the two concerns from fighting: several sub-routes are absent
// from `AppRoutesList` but *are* registered, because addRoute() expands
// `Profile.subRoutes` itself.
const flattenRoutes = (routes: Array<Route>): Array<Route> =>
  routes.flatMap((route) =>
    route.subRoutes ? [route, ...flattenRoutes(Object.values(route.subRoutes))] : [route]
  );

// Static paths before parameterised ones, so `/dashboard/trusts` wins over
// `/dashboard/trusts/:id`.
const titleRoutes = flattenRoutes(AppRoutesList ?? [])
  .filter((route) => route.isEnabled)
  .sort((a, b) => Number(a.path.includes(":")) - Number(b.path.includes(":")));

const matchRoute = (pathname: string) =>
  titleRoutes.find((route) => matchPath(route.path, pathname) !== null);

// Canonical link to a land's detail view. The full LandPage detail now renders
// inline on Explore (over the live map) rather than on the standalone
// `/asset/:tokenId` route, so every in-app "open this land" link points here.
// `?asset=<id>` is read by Explore to reveal the embedded detail (distinct from
// `?id=`, which only opens the "show on map" popup). The standalone route still
// exists for direct loads / shared old URLs.
const landDetailLink = (tokenId: string | number): string =>
  `${AppRoutes.Explore.path}?asset=${tokenId}`;

export { AppRoutes, AppRoutesList, matchRoute, landDetailLink };
